import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LOGIN_PATH } from "@/const";
import { trpc } from "@/lib/trpc";
import { 
  BarChart3,
  BookOpen, 
  Calendar,
  Copy,
  Download,
  Loader2, 
  Users, 
  Plus,
  Edit,
  Trash2,
  Upload,
  Settings,
  Award,
  Megaphone,
  LogOut
} from "lucide-react";
import { Redirect, useLocation } from "wouter";
import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";
import { PieChart, Pie, Cell } from "recharts";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      toast.success("ログアウトしました");
      setLocation("/");
    },
  });
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [showGiftDialog, setShowGiftDialog] = useState(false);
  const [showLevelDialog, setShowLevelDialog] = useState(false);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const [editingLevel, setEditingLevel] = useState<any>(null);
  const [editingSession, setEditingSession] = useState<any>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number>(1);
  const [giftText, setGiftText] = useState("");
  const [analyticsSessionFilter, setAnalyticsSessionFilter] = useState<number | undefined>(undefined);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<any>(null);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", isPublished: false });
  const [usersPage, setUsersPage] = useState(1);
  const [attemptsPage, setAttemptsPage] = useState(1);
  const [registrationsPage, setRegistrationsPage] = useState(1);
  const [badgesPage, setBadgesPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const [attemptsSearch, setAttemptsSearch] = useState("");
  const [registrationsSearch, setRegistrationsSearch] = useState("");
  const [badgesSearch, setBadgesSearch] = useState("");
  const PAGE_SIZE = 20;

  const [questionForm, setQuestionForm] = useState({
    sessionId: 1,
    questionText: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctAnswer: "A" as "A" | "B" | "C" | "D",
    explanation: "",
  });


  const [levelForm, setLevelForm] = useState({
    name: "",
    displayName: "",
    description: "",
    badgeImageUrl: "",
    syllabusContent: "",
    prerequisiteLevelId: null as number | null,
    price: null as number | null,
  });

  const [sessionForm, setSessionForm] = useState({
    levelId: 0,
    name: "",
    displayName: "",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    duration: 40,
    passingScore: 70,
    questionCount: 20,
    examDate: "",
    examStartTime: "",
    examEndTime: "",
    examAccessHours: "",
    badgeImageUrl: "",
    badgeName: "",
    badgeValidityMonths: "",
    giftText: "",
  });

  const { data: levels, refetch: refetchLevels } = trpc.examLevels.list.useQuery();
  const { data: sessions, refetch: refetchSessions } = trpc.examSessions.list.useQuery();
  const { data: allSchedules, refetch: refetchSchedules } = trpc.examSchedules.list.useQuery();
  const { data: questions, isLoading: questionsLoading, refetch: refetchQuestions } = trpc.examQuestions.listForAdmin.useQuery(
    { sessionId: selectedSessionId },
    { enabled: !!user && user.role === "admin" }
  );

  const { data: adminUsers } = trpc.admin.users.useQuery(undefined, { enabled: !!user && user.role === "admin" });
  const { data: adminRegistrations, refetch: refetchRegistrations } = trpc.admin.registrations.useQuery(undefined, { enabled: !!user && user.role === "admin" });
  const { data: adminAttempts, refetch: refetchAttempts } = trpc.admin.attempts.useQuery(undefined, { enabled: !!user && user.role === "admin" });
  const { data: adminBadges, refetch: refetchBadges } = trpc.admin.badges.useQuery(undefined, { enabled: !!user && user.role === "admin" });
  const { data: analytics, isLoading: analyticsLoading } = trpc.admin.analytics.useQuery(
    analyticsSessionFilter ? { sessionId: analyticsSessionFilter } : undefined,
    { enabled: !!user && user.role === "admin" }
  );

  const { data: allAnnouncements = [], refetch: refetchAnnouncements } = trpc.announcements.listAll.useQuery(
    undefined,
    { enabled: !!user && user.role === "admin" }
  );

  const createAnnouncement = trpc.announcements.create.useMutation({
    onSuccess: () => {
      toast.success("お知らせを作成しました");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: "", content: "", isPublished: false });
      setEditingAnnouncement(null);
      refetchAnnouncements();
    },
    onError: () => toast.error("作成に失敗しました"),
  });

  const updateAnnouncement = trpc.announcements.update.useMutation({
    onSuccess: () => {
      toast.success("お知らせを更新しました");
      setShowAnnouncementDialog(false);
      setAnnouncementForm({ title: "", content: "", isPublished: false });
      setEditingAnnouncement(null);
      refetchAnnouncements();
    },
    onError: () => toast.error("更新に失敗しました"),
  });

  const deleteAnnouncement = trpc.announcements.delete.useMutation({
    onSuccess: () => {
      toast.success("お知らせを削除しました");
      refetchAnnouncements();
    },
    onError: () => toast.error("削除に失敗しました"),
  });

  const createQuestion = trpc.examQuestions.create.useMutation({
    onSuccess: () => {
      toast.success("問題を作成しました");
      setShowQuestionDialog(false);
      resetQuestionForm();
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "問題の作成に失敗しました");
    },
  });

  const updateQuestion = trpc.examQuestions.update.useMutation({
    onSuccess: () => {
      toast.success("問題を更新しました");
      setShowQuestionDialog(false);
      setEditingQuestion(null);
      resetQuestionForm();
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "問題の更新に失敗しました");
    },
  });

  const deleteQuestion = trpc.examQuestions.delete.useMutation({
    onSuccess: () => {
      toast.success("問題を削除しました");
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "問題の削除に失敗しました");
    },
  });

  const importGift = trpc.examQuestions.importGIFT.useMutation({
    onSuccess: (data: any) => {
      let msg = `${data.count}問の問題をインポートしました`;
      if (data.skipped > 0) {
        msg += `（重複 ${data.skipped}問をスキップ）`;
      }
      if (data.count === 0 && data.skipped > 0) {
        toast.info(`すべての問題が既に登録済みです（${data.skipped}問スキップ）`);
      } else {
        toast.success(msg);
      }
      setShowGiftDialog(false);
      setGiftText("");
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "インポートに失敗しました");
    },
  });

  const createLevel = trpc.examLevels.create.useMutation({
    onSuccess: () => {
      toast.success("試験レベルを作成しました");
      setShowLevelDialog(false);
      resetLevelForm();
      refetchLevels();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験レベルの作成に失敗しました");
    },
  });

  const updateLevel = trpc.examLevels.update.useMutation({
    onSuccess: () => {
      toast.success("試験レベルを更新しました");
      setShowLevelDialog(false);
      setEditingLevel(null);
      resetLevelForm();
      refetchLevels();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験レベルの更新に失敗しました");
    },
  });

  const deleteLevel = trpc.examLevels.delete.useMutation({
    onSuccess: () => {
      toast.success("試験レベルを削除しました");
      refetchLevels();
      refetchSessions();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験レベルの削除に失敗しました");
    },
  });

  const createSession = trpc.examSessions.create.useMutation({
    onSuccess: async (data) => {
      toast.success("試験セッションを作成しました");
      if (sessionForm.giftText.trim() && data.id) {
        try {
          const result = await importGift.mutateAsync({
            sessionId: data.id,
            giftText: sessionForm.giftText,
          });
          toast.success(`${result.count}問をインポートしました`);
          refetchQuestions();
        } catch (e: any) {
          toast.error(e.message || "問題のインポートに失敗しました");
        }
      }
      setShowSessionDialog(false);
      setEditingSession(null);
      setSessionForm({
        levelId: 0, name: "", displayName: "",
        year: new Date().getFullYear(), month: new Date().getMonth() + 1,
        duration: 40, passingScore: 70, questionCount: 20,
        examDate: "", examStartTime: "", examEndTime: "", examAccessHours: "",
        badgeImageUrl: "", badgeName: "", badgeValidityMonths: "",
        giftText: "",
      });
      refetchSessions();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験セッションの作成に失敗しました");
    },
  });

  const updateSession = trpc.examSessions.update.useMutation({
    onSuccess: () => {
      toast.success("試験セッションを更新しました");
      setShowSessionDialog(false);
      setEditingSession(null);
      refetchSessions();
      refetchSchedules();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験セッションの更新に失敗しました");
    },
  });

  const duplicateSession = trpc.examSessions.duplicate.useMutation({
    onSuccess: () => {
      toast.success("セッションを複製しました");
      refetchSessions();
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "セッションの複製に失敗しました");
    },
  });

  const deleteSession = trpc.examSessions.delete.useMutation({
    onSuccess: () => {
      toast.success("試験セッションを削除しました");
      refetchSessions();
      refetchQuestions();
    },
    onError: (error: any) => {
      toast.error(error.message || "試験セッションの削除に失敗しました");
    },
  });

  const createSchedule = trpc.examSchedules.create.useMutation({
    onSuccess: () => {
      toast.success("日程を追加しました");
      refetchSchedules();
    },
    onError: (error: any) => {
      toast.error(error.message || "日程の追加に失敗しました");
    },
  });

  const deleteSchedule = trpc.examSchedules.delete.useMutation({
    onSuccess: () => {
      toast.success("日程を削除しました");
      refetchSchedules();
    },
    onError: (error: any) => {
      toast.error(error.message || "日程の削除に失敗しました");
    },
  });

  const resetAttemptMutation = trpc.admin.resetAttempt.useMutation({
    onSuccess: () => {
      toast.success("受験履歴をリセットしました");
      refetchAttempts();
      refetchRegistrations();
      refetchBadges();
    },
    onError: (error: any) => {
      toast.error(error.message || "リセットに失敗しました");
    },
  });

  const resetQuestionForm = () => {
    setQuestionForm({
      sessionId: 1,
      questionText: "",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      correctAnswer: "A",
      explanation: "",
    });
  };

  const resetLevelForm = () => {
    setLevelForm({
      name: "",
      displayName: "",
      description: "",
      badgeImageUrl: "",
      syllabusContent: "",
      prerequisiteLevelId: null,
      price: null,
    });
  };

  const handleQuestionSubmit = () => {
    if (editingQuestion) {
      updateQuestion.mutate({
        id: editingQuestion.id,
        questionText: questionForm.questionText,
        optionA: questionForm.optionA,
        optionB: questionForm.optionB,
        optionC: questionForm.optionC,
        optionD: questionForm.optionD,
        correctAnswer: questionForm.correctAnswer,
        explanation: questionForm.explanation,
        sessionId: questionForm.sessionId,
      });
    } else {
      createQuestion.mutate(questionForm);
    }
  };

  const handleLevelSubmit = () => {
    const normalizedName = levelForm.name.trim();
    const normalizedDisplayName = levelForm.displayName.trim();

    if (!normalizedName) {
      toast.error("レベル名を入力してください");
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(normalizedName)) {
      toast.error("レベル名は英数字・ハイフン・アンダースコアのみ使用できます");
      return;
    }

    if (!normalizedDisplayName) {
      toast.error("表示名を入力してください");
      return;
    }

    const payload = {
      ...levelForm,
      name: normalizedName,
      displayName: normalizedDisplayName,
    };

    if (editingLevel) {
      updateLevel.mutate({
        id: editingLevel.id,
        ...payload,
      });
    } else {
      createLevel.mutate(payload);
    }
  };

  const handleGiftImport = () => {
    importGift.mutate({
      sessionId: selectedSessionId,
      giftText,
    });
  };

  const utils = trpc.useUtils();

  const handleGiftDownload = async () => {
    try {
      const result = await utils.examQuestions.exportGIFT.fetch({ sessionId: selectedSessionId });
      if (result.questionCount === 0) {
        toast.info("エクスポートする問題がありません");
        return;
      }
      const blob = new Blob([result.giftText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.sessionName}_questions.gift.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${result.questionCount}問をGIFT形式でダウンロードしました`);
    } catch (err: any) {
      toast.error(err.message || "ダウンロードに失敗しました");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return <Redirect to={LOGIN_PATH} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">管理者ダッシュボード</h1>
            <Button variant="outline" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              <LogOut className="w-4 h-4 mr-2" />
              ログアウト
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="questions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5">
            <TabsTrigger value="questions">
              <BookOpen className="w-4 h-4 mr-2" />
              問題管理
            </TabsTrigger>
            <TabsTrigger value="levels">
              <Settings className="w-4 h-4 mr-2" />
              試験設定
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              受験者管理
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              分析・CSV
            </TabsTrigger>
            <TabsTrigger value="announcements">
              <Megaphone className="w-4 h-4 mr-2" />
              お知らせ
            </TabsTrigger>
          </TabsList>

          {/* 問題管理タブ */}
          <TabsContent value="questions" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>試験問題管理</CardTitle>
                    <CardDescription>試験問題の作成、編集、削除ができます</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleGiftDownload} variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      GIFT形式でダウンロード
                    </Button>
                    <Button onClick={() => setShowGiftDialog(true)} variant="outline" size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      GIFT形式でインポート
                    </Button>
                    <Button size="sm" onClick={() => {
                      setEditingQuestion(null);
                      resetQuestionForm();
                      setShowQuestionDialog(true);
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      問題を追加
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 space-y-2">
                  <Label>試験セッションを選択</Label>
                  <Select
                    value={selectedSessionId.toString()}
                    onValueChange={(value) => setSelectedSessionId(Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sessions?.map((session) => (
                        <SelectItem key={session.id} value={session.id.toString()}>
                          {session.displayName || session.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!questionsLoading && questions && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-md flex items-center justify-between">
                    <span className="text-sm font-medium">
                      登録済み問題数: <span className="text-primary font-bold">{questions.length}問</span>
                      {sessions && (() => {
                        const s = sessions.find((s: any) => s.id === selectedSessionId);
                        return s ? <span className="text-gray-500 ml-2">（出題数: {s.questionCount}問）</span> : null;
                      })()}
                    </span>
                  </div>
                )}

                {questionsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : questions && questions.length > 0 ? (
                  <div className="space-y-4">
                    {questions.map((question: any, index: number) => (
                      <Card key={question.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-base">問題 {index + 1}</CardTitle>
                              <p className="text-sm text-gray-600 mt-2">{question.questionText}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingQuestion(question);
                                  setQuestionForm({
                                    sessionId: question.sessionId,
                                    questionText: question.questionText,
                                    optionA: question.optionA,
                                    optionB: question.optionB,
                                    optionC: question.optionC,
                                    optionD: question.optionD,
                                    correctAnswer: question.correctAnswer,
                                    explanation: question.explanation || "",
                                  });
                                  setShowQuestionDialog(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (confirm("この問題を削除しますか？")) {
                                    deleteQuestion.mutate({ id: question.id });
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>A. {question.optionA}</div>
                            <div>B. {question.optionB}</div>
                            <div>C. {question.optionC}</div>
                            <div>D. {question.optionD}</div>
                          </div>
                          <div className="mt-2 text-sm text-green-600 font-semibold">
                            正解: {question.correctAnswer}
                          </div>
                          {question.explanation && (
                            <div className="mt-1 text-xs text-gray-500">
                              解説: {question.explanation}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    問題がまだ登録されていません
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 試験設定タブ */}
          <TabsContent value="levels" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>試験レベル設定</CardTitle>
                    <CardDescription>試験レベルの作成、編集、バッジ設定ができます</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingLevel(null);
                    resetLevelForm();
                    setShowLevelDialog(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    レベルを追加
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {levels && levels.length > 0 ? (
                  <div className="space-y-4">
                    {levels.map((level) => {
                      const levelSessions = sessions?.filter(s => s.levelId === level.id) || [];
                      return (
                        <Card key={level.id}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="flex items-center gap-2">
                                  <Award className="w-5 h-5" />
                                  {level.displayName}
                                </CardTitle>
                                <CardDescription>{level.description}</CardDescription>
                              </div>
                              <div className="flex gap-2 shrink-0">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSessionForm({
                                      levelId: level.id,
                                      name: "",
                                      displayName: "",
                                      year: new Date().getFullYear(),
                                      month: new Date().getMonth() + 1,
                                      duration: 40,
                                      passingScore: 70,
                                      questionCount: 20,
                                      examDate: "",
                                      examStartTime: "",
                                      examEndTime: "",
                                      examAccessHours: "",
                                      badgeImageUrl: "",
                                      badgeName: "",
                                      badgeValidityMonths: "",
                                      giftText: "",
                                    });
                                    setShowSessionDialog(true);
                                  }}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  セッション追加
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingLevel(level);
                                    setLevelForm({
                                      name: level.name,
                                      displayName: level.displayName,
                                      description: level.description || "",
                                      badgeImageUrl: level.badgeImageUrl || "",
                                      syllabusContent: level.syllabusContent || "",
                                      prerequisiteLevelId: level.prerequisiteLevelId ?? null,
                                      price: level.price ?? null,
                                    });
                                    setShowLevelDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    if (window.confirm(`「${level.displayName}」を削除しますか？`)) {
                                      deleteLevel.mutate({ id: level.id });
                                    }
                                  }}
                                  disabled={deleteLevel.isPending}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          {levelSessions.length > 0 && (
                            <CardContent>
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-500 mb-2">試験セッション</div>
                                {levelSessions.map((session) => {
                                  return (
                                  <div key={session.id} className="p-3 bg-gray-50 rounded-lg border space-y-2">
                                    <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm truncate">{session.displayName || session.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {session.year}年{session.month}月 / {session.duration}分 / 合格点{session.passingScore}点 / {session.questionCount}問
                                      </div>
                                      {session.badgeName && (
                                        <div className="text-xs text-purple-600 flex items-center gap-1">
                                          🏅 バッジ: {session.badgeName}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setEditingSession(session);
                                          setSessionForm({
                                            levelId: session.levelId,
                                            name: session.name,
                                            displayName: session.displayName || "",
                                            year: session.year,
                                            month: session.month,
                                            duration: session.duration,
                                            passingScore: session.passingScore,
                                            questionCount: session.questionCount,
                                            examDate: session.examDate || "",
                                            examStartTime: session.examStartTime || "",
                                            examEndTime: session.examEndTime || "",
                                            examAccessHours: session.examAccessHours ? String(session.examAccessHours) : "",
                                            badgeImageUrl: session.badgeImageUrl || "",
                                            badgeName: session.badgeName || "",
                                            badgeValidityMonths: session.badgeValidityMonths ? String(session.badgeValidityMonths) : "",
                                            giftText: "",
                                          });
                                          setShowSessionDialog(true);
                                        }}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          if (window.confirm(`「${session.displayName || session.name}」を複製しますか？\n問題も一緒にコピーされます。`)) {
                                            duplicateSession.mutate({ id: session.id });
                                          }
                                        }}
                                        disabled={duplicateSession.isPending}
                                      >
                                        <Copy className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => {
                                          if (window.confirm(`「${session.displayName || session.name}」を削除しますか？\n関連する問題と日程も削除されます。`)) {
                                            deleteSession.mutate({ id: session.id });
                                          }
                                        }}
                                        disabled={deleteSession.isPending}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                    </div>
                                    {session.examDate && (
                                    <div className="border-t pt-2">
                                      <div className="flex items-center gap-1 text-xs text-gray-500">
                                        <Calendar className="w-3 h-3" />
                                        試験日: {session.examDate}
                                        {session.examStartTime && <span> {session.examStartTime}〜</span>}
                                        {session.examAccessHours && session.examStartTime && (() => {
                                          const [h, m] = session.examStartTime!.split(":").map(Number);
                                          return `${String(h + session.examAccessHours).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                                        })()}
                                      </div>
                                    </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    試験レベルがまだ登録されていません
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 受験者管理タブ */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>登録ユーザー一覧</CardTitle>
                <CardDescription>システムに登録されたユーザーの一覧です</CardDescription>
              </CardHeader>
              <CardContent>
                {!adminUsers || adminUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">ユーザーがいません</div>
                ) : (() => {
                  const filteredUsers = usersSearch
                    ? adminUsers.filter(u => {
                        const q = usersSearch.toLowerCase();
                        return (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
                      })
                    : adminUsers;
                  return (
                  <div className="overflow-x-auto">
                    <Input placeholder="名前・メールで検索" value={usersSearch} onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }} className="mb-3 max-w-xs" />
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pr-4">ID</th>
                          <th className="pb-2 pr-4">名前</th>
                          <th className="pb-2 pr-4">メール</th>
                          <th className="pb-2 pr-4">権限</th>
                          <th className="pb-2 pr-4">登録日</th>
                          <th className="pb-2">最終ログイン</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.slice((usersPage - 1) * PAGE_SIZE, usersPage * PAGE_SIZE).map((u) => (
                          <tr key={u.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 pr-4">{u.id}</td>
                            <td className="py-2 pr-4">{u.name}</td>
                            <td className="py-2 pr-4">{u.email}</td>
                            <td className="py-2 pr-4">
                              <span className={`px-2 py-0.5 rounded text-xs ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                                {u.role === "admin" ? "管理者" : "一般"}
                              </span>
                            </td>
                            <td className="py-2 pr-4">{u.createdAt ? new Date(u.createdAt).toLocaleDateString("ja-JP") : "-"}</td>
                            <td className="py-2">{u.lastSignedIn ? new Date(u.lastSignedIn).toLocaleDateString("ja-JP") : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length > PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">{filteredUsers.length}件中 {(usersPage - 1) * PAGE_SIZE + 1}〜{Math.min(usersPage * PAGE_SIZE, filteredUsers.length)}件</div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)}>前へ</Button>
                          <Button variant="outline" size="sm" disabled={usersPage * PAGE_SIZE >= filteredUsers.length} onClick={() => setUsersPage(p => p + 1)}>次へ</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>受験履歴</CardTitle>
                <CardDescription>全ユーザーの受験結果一覧です</CardDescription>
              </CardHeader>
              <CardContent>
                {!adminAttempts || adminAttempts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">受験履歴がありません</div>
                ) : (() => {
                  const filteredAttempts = attemptsSearch
                    ? adminAttempts.filter(a => {
                        const q = attemptsSearch.toLowerCase();
                        const attemptUser = adminUsers?.find(u => u.id === a.userId);
                        const attemptSession = sessions?.find(s => s.id === a.sessionId);
                        return (attemptUser?.name?.toLowerCase().includes(q) || attemptSession?.displayName?.toLowerCase().includes(q) || attemptSession?.name?.toLowerCase().includes(q));
                      })
                    : adminAttempts;
                  return (
                  <div className="overflow-x-auto">
                    <Input placeholder="受験者名・セッション名で検索" value={attemptsSearch} onChange={e => { setAttemptsSearch(e.target.value); setAttemptsPage(1); }} className="mb-3 max-w-xs" />
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pr-4">受験者</th>
                          <th className="pb-2 pr-4">試験セッション</th>
                          <th className="pb-2 pr-4">開始日時</th>
                          <th className="pb-2 pr-4">完了日時</th>
                          <th className="pb-2 pr-4">スコア</th>
                          <th className="pb-2 pr-4">結果</th>
                          <th className="pb-2">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttempts.slice((attemptsPage - 1) * PAGE_SIZE, attemptsPage * PAGE_SIZE).map((attempt) => {
                          const attemptUser = adminUsers?.find(u => u.id === attempt.userId);
                          const attemptSession = sessions?.find(s => s.id === attempt.sessionId);
                          return (
                            <tr key={attempt.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 pr-4">{attemptUser?.name || `ID:${attempt.userId}`}</td>
                              <td className="py-2 pr-4">{attemptSession?.displayName || attemptSession?.name || `ID:${attempt.sessionId}`}</td>
                              <td className="py-2 pr-4">{new Date(attempt.startedAt).toLocaleString("ja-JP")}</td>
                              <td className="py-2 pr-4">{attempt.completedAt ? new Date(attempt.completedAt).toLocaleString("ja-JP") : "未完了"}</td>
                              <td className="py-2 pr-4">{attempt.score !== null ? `${attempt.score}点` : "-"}</td>
                              <td className="py-2 pr-4">
                                {attempt.passed === null ? (
                                  <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">受験中</span>
                                ) : attempt.passed ? (
                                  <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">合格</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">不合格</span>
                                )}
                              </td>
                              <td className="py-2">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`${attemptUser?.name || ""}の受験履歴をリセットしますか？\nこの操作は取り消せません。`)) {
                                      resetAttemptMutation.mutate({ attemptId: attempt.id });
                                    }
                                  }}
                                  disabled={resetAttemptMutation.isPending}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  リセット
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredAttempts.length > PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">{filteredAttempts.length}件中 {(attemptsPage - 1) * PAGE_SIZE + 1}〜{Math.min(attemptsPage * PAGE_SIZE, filteredAttempts.length)}件</div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={attemptsPage <= 1} onClick={() => setAttemptsPage(p => p - 1)}>前へ</Button>
                          <Button variant="outline" size="sm" disabled={attemptsPage * PAGE_SIZE >= filteredAttempts.length} onClick={() => setAttemptsPage(p => p + 1)}>次へ</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>受験申込一覧</CardTitle>
                <CardDescription>全ユーザーの受験申込状況です</CardDescription>
              </CardHeader>
              <CardContent>
                {!adminRegistrations || adminRegistrations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">受験申込がありません</div>
                ) : (() => {
                  const filteredRegistrations = registrationsSearch
                    ? adminRegistrations.filter(r => {
                        const q = registrationsSearch.toLowerCase();
                        const regUser = adminUsers?.find(u => u.id === r.userId);
                        const regSession = sessions?.find(s => s.id === r.sessionId);
                        return (regUser?.name?.toLowerCase().includes(q) || regSession?.displayName?.toLowerCase().includes(q) || regSession?.name?.toLowerCase().includes(q));
                      })
                    : adminRegistrations;
                  return (
                  <div className="overflow-x-auto">
                    <Input placeholder="受験者名・セッション名で検索" value={registrationsSearch} onChange={e => { setRegistrationsSearch(e.target.value); setRegistrationsPage(1); }} className="mb-3 max-w-xs" />
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pr-4">受験者</th>
                          <th className="pb-2 pr-4">試験セッション</th>
                          <th className="pb-2 pr-4">ステータス</th>
                          <th className="pb-2">申込日</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRegistrations.slice((registrationsPage - 1) * PAGE_SIZE, registrationsPage * PAGE_SIZE).map((reg) => {
                          const regUser = adminUsers?.find(u => u.id === reg.userId);
                          const regSession = sessions?.find(s => s.id === reg.sessionId);
                          return (
                            <tr key={reg.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 pr-4">{regUser?.name || `ID:${reg.userId}`}</td>
                              <td className="py-2 pr-4">{regSession?.displayName || regSession?.name || `ID:${reg.sessionId}`}</td>
                              <td className="py-2 pr-4">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  reg.status === "completed" ? "bg-green-100 text-green-700" :
                                  reg.status === "cancelled" ? "bg-gray-100 text-gray-500" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                  {reg.status === "pending" ? "申込済" : reg.status === "completed" ? "完了" : "キャンセル"}
                                </span>
                              </td>
                              <td className="py-2">{new Date(reg.createdAt).toLocaleDateString("ja-JP")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredRegistrations.length > PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">{filteredRegistrations.length}件中 {(registrationsPage - 1) * PAGE_SIZE + 1}〜{Math.min(registrationsPage * PAGE_SIZE, filteredRegistrations.length)}件</div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={registrationsPage <= 1} onClick={() => setRegistrationsPage(p => p - 1)}>前へ</Button>
                          <Button variant="outline" size="sm" disabled={registrationsPage * PAGE_SIZE >= filteredRegistrations.length} onClick={() => setRegistrationsPage(p => p + 1)}>次へ</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>バッジ発行一覧</CardTitle>
                <CardDescription>合格者に発行されたバッジの一覧です</CardDescription>
              </CardHeader>
              <CardContent>
                {!adminBadges || adminBadges.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">バッジの発行実績がありません</div>
                ) : (() => {
                  const filteredBadges = badgesSearch
                    ? adminBadges.filter(b => {
                        const q = badgesSearch.toLowerCase();
                        const badgeUser = adminUsers?.find(u => u.id === b.userId);
                        const badgeLevel = levels?.find(l => l.id === b.levelId);
                        return (badgeUser?.name?.toLowerCase().includes(q) || badgeLevel?.displayName?.toLowerCase().includes(q));
                      })
                    : adminBadges;
                  return (
                  <div className="overflow-x-auto">
                    <Input placeholder="取得者名・レベル名で検索" value={badgesSearch} onChange={e => { setBadgesSearch(e.target.value); setBadgesPage(1); }} className="mb-3 max-w-xs" />
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pr-4">取得者</th>
                          <th className="pb-2 pr-4">試験レベル</th>
                          <th className="pb-2">発行日</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBadges.slice((badgesPage - 1) * PAGE_SIZE, badgesPage * PAGE_SIZE).map((badge) => {
                          const badgeUser = adminUsers?.find(u => u.id === badge.userId);
                          const badgeLevel = levels?.find(l => l.id === badge.levelId);
                          return (
                            <tr key={badge.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 pr-4">{badgeUser?.name || `ID:${badge.userId}`}</td>
                              <td className="py-2 pr-4">
                                <span className="flex items-center gap-1">
                                  <Award className="w-4 h-4 text-yellow-500" />
                                  {badgeLevel?.displayName || `ID:${badge.levelId}`}
                                </span>
                              </td>
                              <td className="py-2">{new Date(badge.issuedAt).toLocaleDateString("ja-JP")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredBadges.length > PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">{filteredBadges.length}件中 {(badgesPage - 1) * PAGE_SIZE + 1}〜{Math.min(badgesPage * PAGE_SIZE, filteredBadges.length)}件</div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={badgesPage <= 1} onClick={() => setBadgesPage(p => p - 1)}>前へ</Button>
                          <Button variant="outline" size="sm" disabled={badgesPage * PAGE_SIZE >= filteredBadges.length} onClick={() => setBadgesPage(p => p + 1)}>次へ</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 分析タブ */}
          <TabsContent value="analytics" className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="whitespace-nowrap font-medium">試験セッション:</Label>
              <Select
                value={analyticsSessionFilter?.toString() ?? "all"}
                onValueChange={(value) => setAnalyticsSessionFilter(value === "all" ? undefined : Number(value))}
              >
                <SelectTrigger className="w-[280px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべてのセッション</SelectItem>
                  {(sessions ?? []).map((s: any) => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.displayName || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {analyticsSessionFilter && (
                <Button variant="ghost" size="sm" onClick={() => setAnalyticsSessionFilter(undefined)}>
                  クリア
                </Button>
              )}
            </div>
            {analyticsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : analytics ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-primary">{analytics.summary.totalUsers}</div>
                      <div className="text-xs text-gray-500 mt-1">登録ユーザー数{analyticsSessionFilter ? "（全体）" : ""}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-blue-600">{analytics.summary.totalRegistrations}</div>
                      <div className="text-xs text-gray-500 mt-1">受験申込数</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-indigo-600">{analytics.summary.totalAttempts}</div>
                      <div className="text-xs text-gray-500 mt-1">総受験回数</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{analytics.summary.totalPassed}</div>
                      <div className="text-xs text-gray-500 mt-1">合格者数</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-emerald-600">{analytics.summary.overallPassRate}%</div>
                      <div className="text-xs text-gray-500 mt-1">全体合格率</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-amber-600">{analytics.summary.avgScore}点</div>
                      <div className="text-xs text-gray-500 mt-1">平均スコア</div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">レベル別合格率</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.levelStats.some(l => l.totalAttempts > 0) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {analytics.levelStats
                            .filter((level) => level.totalAttempts > 0)
                            .map((level) => {
                              const pieData = [
                                { name: "合格", value: level.passed, color: "#22c55e" },
                                { name: "不合格", value: level.failed, color: "#ef4444" },
                              ];

                              return (
                                <div key={level.levelName} className="border rounded-lg p-4">
                                  <div className="text-sm font-medium mb-2">{level.levelName}</div>
                                  <div className="h-[250px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <Pie
                                          data={pieData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="45%"
                                          outerRadius={60}
                                          label={({ name, percent }) => `${name}: ${Math.round(percent * 100)}%`}
                                          labelLine={true}
                                        >
                                          {pieData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                          ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className="text-xs text-gray-500 text-center">
                                    合格率: {level.passRate}%（{level.passed}/{level.totalAttempts}）
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className="h-[300px] flex items-center justify-center text-gray-400">受験データがありません</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">スコア分布</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.scoreDistribution.some(d => d.count > 0) ? (
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.scoreDistribution}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="count" name="人数" fill="#6366f1" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="h-[300px] flex items-center justify-center text-gray-400">受験データがありません</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {analytics.monthlyData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">月別受験推移</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={analytics.monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="attempts" name="受験数" stroke="#6366f1" strokeWidth={2} />
                            <Line type="monotone" dataKey="passed" name="合格" stroke="#22c55e" strokeWidth={2} />
                            <Line type="monotone" dataKey="failed" name="不合格" stroke="#ef4444" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">セッション別成績</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.sessionStats.some(s => s.totalAttempts > 0) ? (
                      <div className="space-y-4">
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.sessionStats.filter(s => s.totalAttempts > 0)}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="sessionName" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="avgScore" name="平均点" fill="#6366f1" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="passingScore" name="合格点" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left p-2">セッション</th>
                                <th className="text-left p-2">レベル</th>
                                <th className="text-right p-2">申込</th>
                                <th className="text-right p-2">受験</th>
                                <th className="text-right p-2">合格</th>
                                <th className="text-right p-2">不合格</th>
                                <th className="text-right p-2">合格率</th>
                                <th className="text-right p-2">平均点</th>
                                <th className="text-right p-2">最高点</th>
                                <th className="text-right p-2">最低点</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.sessionStats.filter(s => s.totalAttempts > 0).map(s => (
                                <tr key={s.sessionId} className="border-b hover:bg-gray-50">
                                  <td className="p-2 font-medium">{s.sessionName}</td>
                                  <td className="p-2 text-gray-500">{s.levelName}</td>
                                  <td className="p-2 text-right">{s.totalRegistrations}</td>
                                  <td className="p-2 text-right">{s.totalAttempts}</td>
                                  <td className="p-2 text-right text-green-600 font-medium">{s.passed}</td>
                                  <td className="p-2 text-right text-red-600 font-medium">{s.failed}</td>
                                  <td className="p-2 text-right">
                                    <span className={`font-bold ${s.passRate >= 50 ? "text-green-600" : "text-red-600"}`}>
                                      {s.passRate}%
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-medium">{s.avgScore}点</td>
                                  <td className="p-2 text-right">{s.maxScore}点</td>
                                  <td className="p-2 text-right">{s.minScore}点</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">受験データがありません</div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>CSVエクスポート</CardTitle>
                    <CardDescription>試験結果や申込データをCSVファイルとしてダウンロードできます</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            const csv = await utils.csvExport.results.fetch();
                            if (typeof csv !== "string") throw new Error("CSVレスポンス形式が不正です");
                            const bom = "\uFEFF";
                            const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `exam_results_${new Date().toISOString().slice(0, 10)}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success("結果CSVをダウンロードしました");
                          } catch {
                            toast.error("CSVのダウンロードに失敗しました");
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        試験結果CSV
                      </Button>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            const csv = await utils.csvExport.registrations.fetch();
                            if (typeof csv !== "string") throw new Error("CSVレスポンス形式が不正です");
                            const bom = "\uFEFF";
                            const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `exam_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success("申込CSVをダウンロードしました");
                          } catch {
                            toast.error("CSVのダウンロードに失敗しました");
                          }
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        受験申込CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">データを読み込めませんでした</div>
            )}
          </TabsContent>

          {/* お知らせ管理タブ */}
          <TabsContent value="announcements" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>お知らせ管理</CardTitle>
                    <CardDescription>トップページに表示するお知らせを管理します</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => {
                    setEditingAnnouncement(null);
                    setAnnouncementForm({ title: "", content: "", isPublished: false });
                    setShowAnnouncementDialog(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    新規追加
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {allAnnouncements.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">お知らせはまだありません</div>
                ) : (
                  <div className="space-y-3">
                    {allAnnouncements.map((a) => (
                      <div key={a.id} className="flex items-start justify-between p-4 border rounded-lg gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${a.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                              {a.isPublished ? "公開中" : "非公開"}
                            </span>
                            <span className="font-medium text-sm truncate">{a.title}</span>
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-2">{a.content}</p>
                          {a.publishedAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              公開日: {new Date(a.publishedAt).toLocaleDateString("ja-JP")}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingAnnouncement(a);
                            setAnnouncementForm({ title: a.title, content: a.content, isPublished: a.isPublished });
                            setShowAnnouncementDialog(true);
                          }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => {
                            if (confirm("このお知らせを削除しますか？")) {
                              deleteAnnouncement.mutate({ id: a.id });
                            }
                          }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* お知らせ作成/編集ダイアログ */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement ? "お知らせを編集" : "お知らせを作成"}</DialogTitle>
            <DialogDescription>タイトルと本文を入力してください</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>タイトル</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="お知らせのタイトル"
                maxLength={200}
              />
            </div>
            <div className="space-y-1.5">
              <Label>本文</Label>
              <Textarea
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="お知らせの内容"
                rows={5}
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isPublished"
                checked={announcementForm.isPublished}
                onChange={(e) => setAnnouncementForm((f) => ({ ...f, isPublished: e.target.checked }))}
                className="w-4 h-4"
              />
              <Label htmlFor="isPublished" className="cursor-pointer">公開する（トップページに表示）</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>キャンセル</Button>
            <Button
              onClick={() => {
                if (!announcementForm.title || !announcementForm.content) return;
                if (editingAnnouncement) {
                  updateAnnouncement.mutate({
                    id: editingAnnouncement.id,
                    title: announcementForm.title,
                    content: announcementForm.content,
                    isPublished: announcementForm.isPublished,
                  });
                } else {
                  createAnnouncement.mutate({
                    title: announcementForm.title,
                    content: announcementForm.content,
                    isPublished: announcementForm.isPublished,
                  });
                }
              }}
              disabled={!announcementForm.title || !announcementForm.content || createAnnouncement.isPending || updateAnnouncement.isPending}
            >
              {editingAnnouncement ? "更新" : "作成"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 問題作成/編集ダイアログ */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? "問題を編集" : "問題を追加"}</DialogTitle>
            <DialogDescription>
              試験問題の詳細を入力してください
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>試験セッション</Label>
              <Select
                value={questionForm.sessionId.toString()}
                onValueChange={(value) => setQuestionForm({ ...questionForm, sessionId: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sessions?.map((session) => (
                    <SelectItem key={session.id} value={session.id.toString()}>
                      {session.displayName || session.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>問題文</Label>
              <Textarea
                value={questionForm.questionText}
                onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>選択肢A</Label>
                <Input
                  value={questionForm.optionA}
                  onChange={(e) => setQuestionForm({ ...questionForm, optionA: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>選択肢B</Label>
                <Input
                  value={questionForm.optionB}
                  onChange={(e) => setQuestionForm({ ...questionForm, optionB: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>選択肢C</Label>
                <Input
                  value={questionForm.optionC}
                  onChange={(e) => setQuestionForm({ ...questionForm, optionC: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>選択肢D</Label>
                <Input
                  value={questionForm.optionD}
                  onChange={(e) => setQuestionForm({ ...questionForm, optionD: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>正解</Label>
              <Select
                value={questionForm.correctAnswer}
                onValueChange={(value: any) => setQuestionForm({ ...questionForm, correctAnswer: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>解説（任意）</Label>
              <Textarea
                value={questionForm.explanation}
                onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={handleQuestionSubmit}>
              {editingQuestion ? "更新" : "作成"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GIFT形式インポートダイアログ */}
      <Dialog open={showGiftDialog} onOpenChange={setShowGiftDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>GIFT形式で問題をインポート</DialogTitle>
            <DialogDescription>
              GIFTファイルをアップロードするか、テキストを貼り付けて問題をインポートできます。既に登録済みの問題（同じ問題文）は自動的にスキップされます。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto max-h-[50vh] pr-1">
            <div className="space-y-1">
              <Label>試験セッション</Label>
              <Select
                value={selectedSessionId.toString()}
                onValueChange={(value) => setSelectedSessionId(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sessions?.map((session) => (
                    <SelectItem key={session.id} value={session.id.toString()}>
                      {session.displayName || session.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>GIFTファイルをアップロード</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                <input
                  type="file"
                  accept=".txt,.gift"
                  className="hidden"
                  id="gift-file-upload"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = ev.target?.result as string;
                      if (text) {
                        setGiftText(text);
                        toast.success(`ファイル「${file.name}」を読み込みました`);
                      }
                    };
                    reader.onerror = () => toast.error("ファイルの読み込みに失敗しました");
                    reader.readAsText(file, "UTF-8");
                    e.target.value = "";
                  }}
                />
                <label htmlFor="gift-file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">クリックしてGIFTファイル（.txt / .gift）を選択</p>
                  <p className="text-xs text-gray-400 mt-1">UTF-8エンコードのファイルをアップロードしてください</p>
                </label>
              </div>
            </div>
            <div className="space-y-1">
              <Label>GIFT形式テキスト（直接入力も可）</Label>
              <Textarea
                value={giftText}
                onChange={(e) => setGiftText(e.target.value)}
                rows={6}
                placeholder="例:&#10;問題文 {&#10;  =正解&#10;  ~誤答1&#10;  ~誤答2&#10;  ~誤答3&#10;}"
                className="font-mono text-sm max-h-[30vh]"
              />
              {giftText && (
                <p className="text-xs text-gray-500">{giftText.split("\n").length} 行読み込み済み</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGiftDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={handleGiftImport} disabled={!giftText.trim()}>
              インポート
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* レベル作成/編集ダイアログ */}
      <Dialog open={showLevelDialog} onOpenChange={setShowLevelDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLevel ? "試験レベルを編集" : "試験レベルを追加"}</DialogTitle>
            <DialogDescription>
              試験レベルの詳細を設定します
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>レベル名（英数字）</Label>
              <Input
                value={levelForm.name}
                onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                placeholder="associate"
              />
            </div>
            <div className="space-y-1">
              <Label>表示名</Label>
              <Input
                value={levelForm.displayName}
                onChange={(e) => setLevelForm({ ...levelForm, displayName: e.target.value })}
                placeholder="Associate（入門レベル）"
              />
            </div>
            <div className="space-y-1">
              <Label>説明</Label>
              <Textarea
                value={levelForm.description}
                onChange={(e) => setLevelForm({ ...levelForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-1">
              <Label>受験料（円）｜空白の場合は無料</Label>
              <Input
                type="number"
                min={0}
                value={levelForm.price ?? ""}
                onChange={(e) => setLevelForm({ ...levelForm, price: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="例: 5000"
              />
            </div>
            <div className="space-y-1">
              <Label>前提合格レベル（任意）</Label>
              <Select
                value={levelForm.prerequisiteLevelId ? String(levelForm.prerequisiteLevelId) : "none"}
                onValueChange={(value) =>
                  setLevelForm({
                    ...levelForm,
                    prerequisiteLevelId: value === "none" ? null : Number(value),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="前提なし" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">前提なし</SelectItem>
                  {(levels ?? [])
                    .filter((l) => !editingLevel || l.id !== editingLevel.id)
                    .map((l) => (
                      <SelectItem key={l.id} value={String(l.id)}>
                        {l.displayName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">指定すると、そのレベル合格者のみ受験可能になります</p>
            </div>
            <div className="space-y-1">
              <Label>シラバス（任意）</Label>
              <Textarea
                value={levelForm.syllabusContent}
                onChange={(e) => setLevelForm({ ...levelForm, syllabusContent: e.target.value })}
                rows={6}
                placeholder="試験範囲、学習内容、出題トピックなどを記載..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLevelDialog(false)}>
              キャンセル
            </Button>
            <Button onClick={handleLevelSubmit}>
              {editingLevel ? "更新" : "作成"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSessionDialog} onOpenChange={(open) => {
        setShowSessionDialog(open);
        if (!open) setEditingSession(null);
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSession ? "試験セッションを編集" : "試験セッションを追加"}</DialogTitle>
            <DialogDescription>
              {editingSession ? "試験セッションの詳細を変更します" : "新しい試験セッションの詳細を設定します"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>セッション名<span className="text-red-500 ml-1">*</span></Label>
              <Input
                required
                value={sessionForm.name}
                onChange={(e) => setSessionForm({ ...sessionForm, name: e.target.value })}
                placeholder="例: Associate 2026年4月試験"
              />
            </div>
            <div className="space-y-1">
              <Label>表示名</Label>
              <Input
                value={sessionForm.displayName}
                onChange={(e) => setSessionForm({ ...sessionForm, displayName: e.target.value })}
                placeholder="Associate 2026年4月試験"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>年</Label>
                <Input
                  type="number"
                  value={sessionForm.year}
                  onChange={(e) => setSessionForm({ ...sessionForm, year: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>月</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={sessionForm.month}
                  onChange={(e) => setSessionForm({ ...sessionForm, month: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>試験時間（分）<span className="text-red-500 ml-1">*</span></Label>
                <Input
                  required
                  type="number"
                  min={1}
                  value={sessionForm.duration}
                  onChange={(e) => setSessionForm({ ...sessionForm, duration: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>合格点<span className="text-red-500 ml-1">*</span></Label>
                <Input
                  required
                  type="number"
                  min={1}
                  max={100}
                  value={sessionForm.passingScore}
                  onChange={(e) => setSessionForm({ ...sessionForm, passingScore: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>問題数<span className="text-red-500 ml-1">*</span></Label>
                <Input
                  required
                  type="number"
                  min={1}
                  value={sessionForm.questionCount}
                  onChange={(e) => setSessionForm({ ...sessionForm, questionCount: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="border-t pt-4">
              <div className="text-sm font-medium mb-3">受験可能日時</div>
              <div className="space-y-3">
                <div>
                  <Label>試験日（YYYY-MM-DD）</Label>
                  <Input
                    type="date"
                    value={sessionForm.examDate}
                    onChange={(e) => setSessionForm({ ...sessionForm, examDate: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>開始時間</Label>
                    <Input
                      type="time"
                      value={sessionForm.examStartTime}
                      onChange={(e) => setSessionForm({ ...sessionForm, examStartTime: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>入室可能時間（時間）</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="例: 4"
                      value={sessionForm.examAccessHours}
                      onChange={(e) => setSessionForm({ ...sessionForm, examAccessHours: e.target.value })}
                    />
                  </div>
                </div>
                {sessionForm.examStartTime && sessionForm.examAccessHours && (
                  <p className="text-xs text-blue-600">
                    入室締切: {(() => {
                      const [h, m] = sessionForm.examStartTime.split(":").map(Number);
                      const endH = h + Number(sessionForm.examAccessHours);
                      return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                    })()}（開始から{sessionForm.examAccessHours}時間後に入室不可）
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  設定すると、指定された日時以外は受験できなくなります。未設定の場合はいつでも受験可能です。
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">合格バッジ設定</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>バッジ名</Label>
                    <Input
                      placeholder="例: AWS Associate 合格バッジ"
                      value={sessionForm.badgeName}
                      onChange={(e) => setSessionForm({ ...sessionForm, badgeName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>バッジ画像</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        className="hidden"
                        id="session-badge-upload"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("ファイルサイズは5MB以下にしてください");
                            return;
                          }
                          const formData = new FormData();
                          formData.append("badge", file);
                          try {
                            const res = await fetch("/api/upload/badge", { method: "POST", body: formData });
                            const data = await res.json();
                            if (data.url) {
                              setSessionForm({ ...sessionForm, badgeImageUrl: data.url });
                              toast.success("バッジ画像をアップロードしました");
                            } else {
                              toast.error(data.error || "アップロードに失敗しました");
                            }
                          } catch {
                            toast.error("アップロードに失敗しました");
                          }
                          e.target.value = "";
                        }}
                      />
                      <label htmlFor="session-badge-upload" className="cursor-pointer">
                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                        <p className="text-sm text-gray-600">クリックして画像を選択</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPEG, GIF, WebP（5MB以下）</p>
                      </label>
                    </div>
                    {sessionForm.badgeImageUrl && (
                      <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                        <img
                          src={sessionForm.badgeImageUrl}
                          alt="バッジプレビュー"
                          className="w-12 h-12 object-contain rounded"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <span className="text-sm text-gray-600">{sessionForm.badgeName || "バッジ名未設定"}</span>
                        <Button variant="ghost" size="sm" onClick={() => setSessionForm({ ...sessionForm, badgeImageUrl: "" })}>✕</Button>
                      </div>
                    )}
                    <Input
                      placeholder="またはURLを直接入力: https://..."
                      value={sessionForm.badgeImageUrl}
                      onChange={(e) => setSessionForm({ ...sessionForm, badgeImageUrl: e.target.value })}
                      className="text-xs"
                    />
                  </div>
                </div>
                  <div className="space-y-1">
                    <Label>バッジ有効期間（月数）</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="例: 12（未設定＝無期限）"
                      value={sessionForm.badgeValidityMonths}
                      onChange={(e) => setSessionForm({ ...sessionForm, badgeValidityMonths: e.target.value })}
                    />
                  </div>
                <p className="text-xs text-gray-500">
                  合格者に付与されるバッジを設定できます。有効期間を設定すると期限付きバッジになります。
                </p>
              </div>
              <div className="space-y-2">
                <Label>GIFT形式で問題をインポート（任意）</Label>
                {editingSession && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm text-amber-800 font-medium">⚠ 注意：既存の問題はすべて削除されます</p>
                    <p className="text-xs text-amber-600 mt-1">ここからGIFTファイルをアップロードすると、このセッションに登録されている既存の問題がすべて削除され、新しい問題に置き換えられます。</p>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    accept=".txt,.gift"
                    className="hidden"
                    id="session-gift-file-upload"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const text = ev.target?.result as string;
                        if (text) {
                          setSessionForm({ ...sessionForm, giftText: text });
                          toast.success(`ファイル「${file.name}」を読み込みました`);
                        }
                      };
                      reader.onerror = () => toast.error("ファイルの読み込みに失敗しました");
                      reader.readAsText(file, "UTF-8");
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="session-gift-file-upload" className="cursor-pointer">
                    <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    <p className="text-sm text-gray-600">GIFTファイルをアップロード（.txt / .gift）</p>
                  </label>
                </div>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md text-sm font-mono"
                  placeholder={`または直接テキストを貼り付け（例）:\n::Q1::問題文{=正解 ~不正解A ~不正解B ~不正解C}`}
                  value={sessionForm.giftText}
                  onChange={(e) => setSessionForm({ ...sessionForm, giftText: e.target.value })}
                />
                {sessionForm.giftText && (
                  <p className="text-xs text-gray-500">{sessionForm.giftText.split("\n").length} 行読み込み済み</p>
                )}
                <p className="text-xs text-gray-500">
                  {editingSession
                    ? "GIFTファイルをアップロードすると既存の問題を全て置き換えます。空欄の場合は問題は変更されません。"
                    : "セッション作成時に問題も一括でインポートできます。空欄の場合はセッションのみ作成されます。"}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSessionDialog(false); setEditingSession(null); }}>
              キャンセル
            </Button>
            <Button
              onClick={async () => {
                if (!sessionForm.name.trim()) { toast.error("セッション名は必須です"); return; }
                if (!sessionForm.duration || sessionForm.duration <= 0) { toast.error("試験時間（分）は1以上の値を入力してください"); return; }
                if (!sessionForm.passingScore || sessionForm.passingScore <= 0) { toast.error("合格点は1以上の値を入力してください"); return; }
                if (!sessionForm.questionCount || sessionForm.questionCount <= 0) { toast.error("問題数は1以上の値を入力してください"); return; }
                
                // 日跨ぎチェック: 開始時間 + 入室可能時間が24時間を超えないこと
                if (sessionForm.examStartTime && sessionForm.examAccessHours) {
                  const [startH, startM] = sessionForm.examStartTime.split(":").map(Number);
                  const accessHours = parseInt(sessionForm.examAccessHours);
                  if (!isNaN(startH) && !isNaN(accessHours)) {
                    if (startH + accessHours > 24) {
                      toast.error(`開始時間（${sessionForm.examStartTime}）と入室可能時間（${accessHours}時間）の設定では日をまたいでしまいます。24時間以内に収まるように設定してください。`);
                      return;
                    }
                  }
                }
                
                if (editingSession) {
                  if (sessionForm.giftText?.trim()) {
                    if (!confirm("既存の問題をすべて削除して、新しい問題に置き換えます。よろしいですか？")) {
                      return;
                    }
                  }
                  updateSession.mutate({
                    id: editingSession.id,
                    name: sessionForm.name,
                    displayName: sessionForm.displayName,
                    year: sessionForm.year,
                    month: sessionForm.month,
                    duration: sessionForm.duration,
                    passingScore: sessionForm.passingScore,
                    questionCount: sessionForm.questionCount,
                    examDate: sessionForm.examDate || null,
                    examStartTime: sessionForm.examStartTime || null,
                    examEndTime: sessionForm.examEndTime || null,
                    examAccessHours: sessionForm.examAccessHours ? parseInt(sessionForm.examAccessHours) : null,
                    badgeImageUrl: sessionForm.badgeImageUrl || null,
                    badgeName: sessionForm.badgeName || null,
                    badgeValidityMonths: sessionForm.badgeValidityMonths ? parseInt(sessionForm.badgeValidityMonths) : null,
                  });
                  if (sessionForm.giftText?.trim()) {
                    importGift.mutate({
                      sessionId: editingSession.id,
                      giftText: sessionForm.giftText,
                      replaceAll: true,
                    });
                  }
                } else {
                  const { giftText, badgeValidityMonths, ...createPayload } = sessionForm;
                  createSession.mutate({
                    ...createPayload,
                    examDate: sessionForm.examDate || undefined,
                    examStartTime: sessionForm.examStartTime || undefined,
                    examEndTime: sessionForm.examEndTime || undefined,
                    examAccessHours: sessionForm.examAccessHours ? parseInt(sessionForm.examAccessHours) : undefined,
                    badgeImageUrl: sessionForm.badgeImageUrl || undefined,
                    badgeName: sessionForm.badgeName || undefined,
                    badgeValidityMonths: badgeValidityMonths ? parseInt(badgeValidityMonths) : undefined,
                  });
                }
              }}
              disabled={!sessionForm.name || !sessionForm.duration || !sessionForm.passingScore || !sessionForm.questionCount || (!editingSession && !sessionForm.levelId) || createSession.isPending || updateSession.isPending}
            >
              {editingSession ? "更新" : "作成"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
